/**
 * QuotesPage — TekStak mount of the shared QuoteBuilder (Phase 4).
 *
 * Same shared component + pricing engine as the GAS app; only the data
 * wiring differs (apiClient with Supabase JWT — the backend bridges the
 * JWT to the user's linked API key, so the SAME quotes appear in both
 * apps). Sheets export lives in GAS (SpreadsheetApp); here export is CSV.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { QuoteBuilder, DealCanvas } from '@techstack/ui';
import { computeQuoteEconomics, segmentEnabled } from '@techstack/core';
import apiClient from '../../lib/apiClient';
import PageHeader from '../shared/PageHeader';
import '@techstack/ui/dist/tokens.css';
import '@techstack/ui/dist/styles/quote-builder.css';
// PAN brand ramps for GTM color coding on PAN content (TekStak keeps
// its OWN brand — these --brand-* vars are namespaced and additive).
import '@techstack/ui/dist/brand.css';
import '@techstack/ui/dist/styles/branding.css';
import './QuotesPage.css';

const DRIVER_LABEL = {
  per_user: 'users',
  per_endpoint: 'endpoints',
  per_gb_day: 'GB/day of ingest',
  per_device: 'devices',
};

const errMsg = (e) => e?.data?.error || e?.message || String(e);

// Executive TCO proposal — CSV fallback (the Sheets writer lives in the
// GAS app). The payload arrives fully computed AND sanitized from
// TcoComparePanel (street only as a band + pre-stripped provenance,
// 'candidate — verify' labels, '(vendor-claimed)' tags) — this just
// flattens rows; it must not alter any figure or label.
function downloadTcoProposalCsv(p) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const usd = (n) => (n == null ? '—' : '$' + Math.round(n).toLocaleString('en-US'));
  const h3 = p.horizons.find((h) => h.horizonYears === 3);
  const h5 = p.horizons.find((h) => h.horizonYears === 5);
  const band = (h) => (h?.streetLowTotal != null ? `${usd(h.streetLowTotal)} – ${usd(h.streetHighTotal)}` : 'no street intel — list only');
  const rows = [
    ['Executive TCO Proposal'],
    ['Prepared for', p.deptAcronym || ''],
    ['Comparison', `Palo Alto Networks vs ${p.vendor}`],
    ['Based on', (p.quoteName || p.quoteId) + (p.estimateNo ? ` (${p.estimateNo.replace(/^EST-/, 'Q-')})` : '')],
    ['Deployment', p.panModels.map((m) => `${m.model}×${m.qty}`).join(' · ') + (p.dominantTermMonths ? ` · ${p.dominantTermMonths}-mo terms dominant` : '')],
    [],
    ['TOTAL COST OF OWNERSHIP', `Palo Alto Networks — customer pricing${p.scenarioName ? ` (${p.scenarioName})` : ''}`, `${p.vendor} — CA price-book list`, `${p.vendor} — estimated street`],
    ['One-time', usd(p.pan?.oneTimeTotal), usd(p.compList.oneTimeTotal), ''],
    ['Recurring / yr', usd(p.pan?.recurringAnnual), usd(p.compList.recurringAnnual), ''],
    ['3-year TCO', usd(h3?.panTotal), usd(h3?.compListTotal), band(h3)],
    ['5-year TCO', usd(h5?.panTotal), usd(h5?.compListTotal), band(h5)],
  ];
  if (p.streetProvenance) rows.push(['', '', '', `Estimated street is a band, not a quote — ${p.streetProvenance}`]);
  const story = p.horizons.find((h) => h.horizonYears === p.storyHorizonYears);
  if (story?.delta != null) {
    rows.push([], [story.delta >= 0
      ? `The platform saves ${usd(story.delta)} vs ${p.vendor} list pricing over ${p.storyHorizonYears} years.`
      : `${p.vendor} list pricing runs ${usd(-story.delta)} under our customer price over ${p.storyHorizonYears} years — the value case below is the platform story, not price.`]);
  }
  p.stories.forEach((s) => {
    rows.push([], [s.headline]);
    if (s.talkTrack) rows.push([s.talkTrack]);
    s.differentiators.forEach((d) => rows.push([`• ${d}`]));
    s.proofPoints.forEach((pp) => rows.push([`• ${pp.claim} — ${pp.source}`]));
  });
  if (p.stack.length) {
    rows.push([], ['HOW THE COMPARISON WAS BUILT — resolved stack']);
    rows.push(['Ours', 'Qty', `${p.vendor} equivalent`, 'Sizing basis', 'SKU', 'Description', 'Term', 'List (extended)', 'Note']);
    p.stack.forEach((s) => {
      const rung = [s.panModel, s.qty, `${s.competitorModel} (${s.mapConfidence} confidence map)`, s.sizingNote || ''];
      if (!s.picks.length) { rows.push([...rung, '', 'no price-book match', '', '', '']); return; }
      s.picks.forEach((pk, j) => rows.push([
        ...(j === 0 ? rung : ['', '', '', '']),
        pk.sku, pk.name, pk.termMonths ? `${pk.termMonths} mo` : 'one-time',
        usd(pk.listExtended), pk.verifyLabel || '',
      ]));
    });
  }
  rows.push([], ['ASSUMPTIONS & SOURCES']);
  p.assumptions.forEach((a) => rows.push([`• ${a}`]));
  p.unresolvedNotes.forEach((u) => rows.push([`• Not included in this comparison: ${u}`]));
  p.citations.forEach((c) => rows.push([`• ${c}`]));
  rows.push([`This proposal is an estimate for planning purposes, not a quote. Formal quotes are issued by the authorized resellers. Palo Alto Networks pricing reflects the contract snapshot cited above; ${p.vendor} figures come from public California price books at list, with any street figure shown only as an estimated band. All pricing is subject to change.`]);
  const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `tco-proposal-vs-${p.vendor.replace(/[^\w.-]+/g, '_')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Fuzzy match: every query token must appear as a substring OR an
// in-order subsequence of the haystack.
function fuzzyMatch(query, haystack) {
  const hay = String(haystack || '').toLowerCase();
  return String(query || '').toLowerCase().split(/\s+/).filter(Boolean).every((tok) => {
    if (hay.includes(tok)) return true;
    let i = 0;
    for (const ch of hay) { if (ch === tok[i]) i++; if (i === tok.length) return true; }
    return false;
  });
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState(null);
  const [quote, setQuote] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [dealOpp, setDealOpp] = useState(null);       // open canvas: { opp } or { solo: quoteId }
  const [openingId, setOpeningId] = useState(null);   // quote being fetched (row feedback)
  const [confirmDelId, setConfirmDelId] = useState(null); // two-step delete arm
  const confirmDelTimer = useRef(null);
  const [dupPendingId, setDupPendingId] = useState(null);  // ⧉ in flight
  const [flashId, setFlashId] = useState(null);            // freshly-duplicated row flash
  const [pasteReport, setPasteReport] = useState(null);    // pinned no-price chip
  const [removedQuote, setRemovedQuote] = useState(null);  // quote-delete undo toast
  const undoQTimer = useRef(null);
  const [deptQuery, setDeptQuery] = useState('');
  const [deptResults, setDeptResults] = useState([]);
  const [catalogQuery, setCatalogQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogTree, setCatalogTree] = useState([]);
  const [browse, setBrowse] = useState(null);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);
  const [saveState, setSaveState] = useState('saved');
  const pendingSaves = useRef(0);
  const [specsBySku, setSpecsBySku] = useState({});
  const [gplDriftBySku, setGplDriftBySku] = useState({});
  // Layer-1 quote review findings (null = not run; 'loading' = in flight).
  const [validation, setValidation] = useState(null);
  useEffect(() => { setValidation(null); }, [quote?.quoteId]);
  const [gplDriftCheckedAt, setGplDriftCheckedAt] = useState(null);
  const [catalogReferenceOnly, setCatalogReferenceOnly] = useState([]);
  const driftChecked = useRef({ contractId: null, skus: new Set() });
  const [skuRules, setSkuRules] = useState(null);
  const [iconsBaseUrl, setIconsBaseUrl] = useState(null);
  const quoteRef = useRef(null);
  quoteRef.current = quote;
  // Guard: a slow mutation response from quote A must never clobber the
  // currently-open quote B or reopen a closed builder (same invariant
  // as the co-edit poll). Returns true when applied.
  const applyQuote = (q) => {
    const cur = quoteRef.current;
    if (!q || !cur || cur.quoteId !== q.quoteId) return false;
    setQuote(q);
    return true;
  };
  const searchTimer = useRef(null);
  const saveTimer = useRef(null);
  const searchSeq = useRef(0);
  // Competition strip (C0): seated incumbents for the open deal's agency.
  const [dealFootprint, setDealFootprint] = useState(undefined); // undefined=hidden, null=loading, object=loaded
  const [footprintErr, setFootprintErr] = useState(null);
  const [cmpTarget, setCmpTarget] = useState(null);
  const [quoteExports, setQuoteExports] = useState([]);
  const footprintCache = useRef({});

  const refreshList = useCallback(() => {
    apiClient.get('/api/v1/quotes')
      .then((r) => setQuotes(r?.quotes || []))
      .catch((e) => { setErr(errMsg(e)); setQuotes([]); });
  }, []);

  useEffect(() => {
    refreshList();
    apiClient.get('/api/v1/gpl/vehicles').then((r) => setVehicles(r?.vehicles || [])).catch(() => {});
    apiClient.get('/api/v1/gpl/bundles').then((r) => setBundles(r?.bundles || [])).catch(() => {});
    apiClient.get('/api/v1/branding').then((r) => {
      setSkuRules(r?.skuRules || null);
      setIconsBaseUrl(r?.iconsBaseUrl || null);
    }).catch(() => {});
  }, [refreshList]);

  // Team co-editing: poll while open; merge teammate edits with attribution.
  useEffect(() => {
    if (!quote?.quoteId) return undefined;
    const id = setInterval(() => {
      const cur = quoteRef.current;
      if (!cur || cur.__hydrating || pendingSaves.current > 0) return;
      apiClient.get(`/api/v1/quotes/${cur.quoteId}`)
        .then((r) => {
          const latest = quoteRef.current;
          if (!r?.quote || !latest || r.quote.quoteId !== latest.quoteId) return;
          if (r.quote.updatedAt !== latest.updatedAt && pendingSaves.current === 0) {
            setQuote(r.quote);
            setInfo('Updated by ' + (r.quote.updatedBy || 'a teammate'));
          }
        })
        .catch(() => {});
    }, 12000);
    return () => clearInterval(id);
  }, [quote?.quoteId]);

  // Seated incumbents follow the open deal canvas: fetch the agency's
  // SCPRS competitive footprint once per agency (cached for the session),
  // and restore this deal's pinned comparison target from localStorage.
  useEffect(() => {
    if (dealOpp === null) { setDealFootprint(undefined); setFootprintErr(null); setCmpTarget(null); return undefined; }
    const dealKey = dealOpp.opp || dealOpp.solo;
    try { setCmpTarget(localStorage.getItem(`qbCmpTarget:${dealKey}`) || null); } catch { setCmpTarget(null); }
    const dq = dealOpp.opp
      ? (quotes || []).filter((q) => q.sfOpportunityId === dealOpp.opp)
      : (quotes || []).filter((q) => q.quoteId === dealOpp.solo);
    const acct = dq[0]?.deptAcronym;
    if (!acct) { setDealFootprint(undefined); setFootprintErr(null); return undefined; }
    if (footprintCache.current[acct]) { setDealFootprint(footprintCache.current[acct]); setFootprintErr(null); return undefined; }
    let stale = false;
    setDealFootprint(null);
    setFootprintErr(null);
    apiClient.get(`/api/v1/competitive/footprint?account=${encodeURIComponent(acct)}`)
      .then((r) => { if (!stale) { footprintCache.current[acct] = r; setDealFootprint(r); } })
      .catch((e) => { if (!stale) { setDealFootprint(undefined); setFootprintErr('Couldn’t load incumbents — ' + errMsg(e)); } });
    return () => { stale = true; };
  }, [dealOpp, quotes]);

  const selectIncumbent = (vendor) => {
    setCmpTarget(vendor);
    const dealKey = dealOpp?.opp || dealOpp?.solo;
    if (!dealKey) return;
    try {
      if (vendor) localStorage.setItem(`qbCmpTarget:${dealKey}`, vendor);
      else localStorage.removeItem(`qbCmpTarget:${dealKey}`);
    } catch { /* storage unavailable — selection stays session-only */ }
  };

  // Export history for the Documents drawer.
  useEffect(() => {
    if (!quote?.quoteId) { setQuoteExports([]); return; }
    apiClient.get(`/api/v1/quotes/${quote.quoteId}/exports`)
      .then((r) => setQuoteExports(r?.exports || []))
      .catch(() => setQuoteExports([]));
  }, [quote?.quoteId]);

  const onRequestSpecs = (sku) => {
    setSpecsBySku((prev) => (prev[sku] !== undefined ? prev : { ...prev, [sku]: 'loading' }));
    apiClient.get('/api/v1/gpl/tech-specs', { params: { sku } })
      .then((r) => setSpecsBySku((prev) => ({ ...prev, [sku]: r?.specs || null })))
      .catch(() => setSpecsBySku((prev) => ({ ...prev, [sku]: null })));
  };

  // GPL drift flags (gpl_reference_diffs): the ETC-vs-public-GPL diff,
  // rebuilt monthly by the backend crons. Checked lazily per SKU — quote
  // lines + catalog results — and cached per vehicle; only flagged SKUs
  // come back, so an absent key means "clean on both files".
  useEffect(() => {
    const cid = quote?.contractId;
    if (!cid) return;
    if (driftChecked.current.contractId !== cid) {
      driftChecked.current = { contractId: cid, skus: new Set() };
      setGplDriftBySku({});
    }
    const skus = new Set();
    (quote.lines || []).forEach((l) => { if (l.kind !== 'header' && l.kind !== 'section' && l.sku) skus.add(l.sku); });
    (catalogResults || []).forEach((r) => { if (r.sku) skus.add(r.sku); });
    const unchecked = [...skus].filter((s) => !driftChecked.current.skus.has(s));
    if (!unchecked.length) return;
    unchecked.forEach((s) => driftChecked.current.skus.add(s));
    apiClient.get('/api/v1/gpl/reference-diffs', { params: { contractId: cid, skus: unchecked.join(',') } })
      .then((r) => {
        if (r?.computedAt) setGplDriftCheckedAt(r.computedAt);
        if (!r?.results?.length) return;
        setGplDriftBySku((prev) => {
          const next = { ...prev };
          r.results.forEach((d) => { next[d.sku] = d; });
          return next;
        });
      })
      .catch(() => {});
  }, [quote?.contractId, quote?.lines, catalogResults]);

  // Browse tree follows the open quote's vehicle.
  useEffect(() => {
    if (!quote?.contractId) { setCatalogTree([]); return; }
    apiClient.get('/api/v1/gpl/catalog-tree', { params: { contractId: quote.contractId } })
      .then((r) => setCatalogTree(r?.categories || []))
      .catch(() => setCatalogTree([]));
    setBrowse(null);
  }, [quote?.contractId]);

  // Background-save tracker (see GAS QuotesView): "Saving…" until the
  // server confirms; failures warn AND refetch so the UI never diverges.
  const trackSave = (promise, quoteId) => {
    pendingSaves.current += 1;
    setSaveState('saving');
    promise
      .then((r) => {
        if (r?.error) throw new Error(r.error);
        pendingSaves.current -= 1;
        if (pendingSaves.current <= 0) { pendingSaves.current = 0; setSaveState('saved'); }
      })
      .catch(() => {
        pendingSaves.current = 0;
        setSaveState('error');
        if (quoteId) apiClient.get(`/api/v1/quotes/${quoteId}`).then((r) => { if (r?.quote) applyQuote(r.quote); }).catch(() => {});
      });
  };

  const applyResult = (r, fallback) => {
    if (r?.quote) { if (applyQuote(r.quote)) setErr(null); }
    else {
      setErr(r?.error || fallback);
      setQuote((prev) => (prev && prev.__hydrating ? null : prev));
    }
  };

  // Optimistic open: paint the builder shell instantly from the list
  // row, skeleton the lines, hydrate when the full quote arrives.
  const openQuote = (quoteId) => {
    setErr(null); setInfo(null); setCatalogQuery(''); setCatalogResults([]); setPasteReport(null);
    const row = (quotes || []).find((q) => q.quoteId === quoteId);
    if (row) {
      setQuote({
        quoteId: row.quoteId, estimateNo: row.estimateNo || null, name: row.name,
        deptAcronym: row.deptAcronym || null, sfOpportunityId: row.sfOpportunityId || null,
        status: row.status, contractId: '', vehicle: row.vehicle || '',
        partnerName: null, sapNumber: row.sapNumber || null,
        registrationStatus: row.registrationStatus || 'none',
        scenarios: [], activeScenario: '', lines: [], pricingAsOf: null,
        taxRatePct: null, __hydrating: true,
      });
    }
    setOpeningId(quoteId);
    apiClient.get(`/api/v1/quotes/${quoteId}`)
      .then((r) => applyResult(r, 'Could not open quote'))
      .catch((e) => { setQuote(null); setErr(errMsg(e)); })
      .finally(() => setOpeningId(null));
  };

  const searchDepts = (q) => {
    setDeptQuery(q);
    if (!q) { setDeptResults([]); return; }
    apiClient.get('/api/v1/departments', { params: { search: q, limit: 8 } })
      .then((r) => setDeptResults(r?.departments || []))
      .catch(() => setDeptResults([]));
  };

  // Customer-first creation: pick the agency, land in the builder.
  // Vehicle defaults to ETC — switchable inside the builder (reprices).
  const createQuoteFor = (dept) => {
    const acronym = dept?.acronym || null;
    const name = (acronym ? acronym + ' — ' : '') + 'Quote ' + new Date().toISOString().slice(0, 10);
    setCreating(false); setDeptQuery(''); setDeptResults([]);
    apiClient.post('/api/v1/quotes', { name, contractId: '1-24-70-19-19', deptAcronym: acronym })
      .then((r) => { applyResult(r, 'Could not create quote'); refreshList(); })
      .catch((e) => setErr(errMsg(e)));
  };

  const onVehicleChange = (contractId) => {
    setInfo('Re-pricing on ' + contractId + '…');
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/reprice`, { contractId })
      .then((r) => {
        if (r?.quote) {
          if (applyQuote(r.quote)) setErr(null);
          const missing = (r.report || []).filter((x) => x.status !== 'repriced');
          setInfo('Re-priced on ' + r.quote.vehicle + ' · ' + r.quote.contractId
            + (missing.length ? ' — no price for: ' + missing.map((x) => x.sku).join(', ') : ''));
        } else { setInfo(null); setErr(r?.error || 'Could not switch vehicle'); }
      })
      .catch((e) => { setInfo(null); setErr(errMsg(e)); });
  };

  const duplicateQuote = (quoteId) => {
    setDupPendingId(quoteId);
    apiClient.post(`/api/v1/quotes/${quoteId}/duplicate`)
      .then((r) => {
        const newId = r?.quote?.quoteId || null;
        return apiClient.get('/api/v1/quotes').then((lr) => {
          if (lr?.quotes) setQuotes(lr.quotes);
          if (newId) { setFlashId(newId); setTimeout(() => setFlashId(null), 1600); }
        });
      })
      .catch((e) => setErr(errMsg(e)))
      .finally(() => setDupPendingId(null));
  };

  const deleteQuote = (quoteId) => {
    const row = (quotes || []).find((q) => q.quoteId === quoteId);
    apiClient.del(`/api/v1/quotes/${quoteId}`)
      .then(() => {
        refreshList();
        setRemovedQuote({ quoteId, name: row?.name || 'quote' });
        clearTimeout(undoQTimer.current);
        undoQTimer.current = setTimeout(() => setRemovedQuote(null), 8000);
      })
      .catch((e) => setErr(errMsg(e)));
  };

  const undoDeleteQuote = () => {
    const q = removedQuote;
    setRemovedQuote(null);
    clearTimeout(undoQTimer.current);
    if (!q) return;
    apiClient.post(`/api/v1/quotes/${q.quoteId}/undelete`)
      .then(() => refreshList())
      .catch((e) => setErr(errMsg(e)));
  };

  // Destructive action = two clicks: first arms ("Delete?"), second
  // commits; the arm auto-disarms after 3s.
  const requestDelete = (quoteId) => {
    clearTimeout(confirmDelTimer.current);
    if (confirmDelId === quoteId) {
      setConfirmDelId(null);
      deleteQuote(quoteId);
    } else {
      setConfirmDelId(quoteId);
      confirmDelTimer.current = setTimeout(() => setConfirmDelId(null), 3000);
    }
  };

  const closeQuote = () => { setQuote(null); setQuotes(null); refreshList(); };

  const onCatalogQueryChange = (q) => {
    setCatalogQuery(q);
    if (q) setBrowse(null);
    clearTimeout(searchTimer.current);
    if (!quote || !q || q.length < 2) { setCatalogResults([]); setCatalogReferenceOnly([]); setCatalogLoading(false); return; }
    setCatalogLoading(true);
    const seq = ++searchSeq.current;
    searchTimer.current = setTimeout(() => {
      apiClient.get('/api/v1/gpl/contract-skus', { params: { contractId: quote.contractId, q, limit: 25 } })
        .then((r) => {
          if (seq !== searchSeq.current) return;
          setCatalogResults(r?.results || []);
          // Matches on the public PAN GPL not yet uploaded to this
          // contract — shown as a flagged "awaiting ETC" section.
          setCatalogReferenceOnly(r?.referenceOnly || []);
        })
        .catch(() => { if (seq === searchSeq.current) { setCatalogResults([]); setCatalogReferenceOnly([]); } })
        .finally(() => { if (seq === searchSeq.current) setCatalogLoading(false); });
    }, 250);
  };

  const onBrowseSelect = (sel) => {
    setBrowse(sel);
    setCatalogQuery('');
    setCatalogReferenceOnly([]);
    if (!sel) { setCatalogResults([]); return; }
    setCatalogLoading(true);
    const seq = ++searchSeq.current;
    apiClient.get('/api/v1/gpl/contract-skus', { params: { contractId: quote.contractId, category: sel.category, model: sel.model || undefined, limit: 50 } })
      .then((r) => { if (seq === searchSeq.current) setCatalogResults(r?.results || []); })
      .catch(() => { if (seq === searchSeq.current) setCatalogResults([]); })
      .finally(() => { if (seq === searchSeq.current) setCatalogLoading(false); });
  };

  const onDealMetaChange = (patch) => {
    setQuote((prev) => ({ ...prev, ...patch }));
    trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}`, { method: 'PATCH', body: patch }), quote.quoteId);
  };

  const onOpportunityIdChange = (sfOpportunityId) => {
    setQuote((prev) => ({ ...prev, sfOpportunityId }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}`, { method: 'PATCH', body: { sfOpportunityId } }), quote.quoteId);
    }, 600);
  };

  // Add handlers RETURN their promise — the builder paints an optimistic
  // pending row and clears it when the save settles.
  // Attach guardrail: resolve support SKUs through the vehicle-priced search.
  const onResolveAttach = (q) =>
    apiClient.get('/api/v1/gpl/contract-skus', { params: { contractId: quote.contractId, q, limit: 25 } })
      .then((r) => r?.results || [])
      .catch(() => []);

  // Term switcher: swap a support/subscription line to the N-year SKU.
  const onLineTermChange = (lineId, years) => {
    setInfo('Switching to the ' + years + '-year SKU…');
    return apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/${lineId}/term`, { years })
      .then((r) => {
        if (r?.quote) {
          if (applyQuote(r.quote)) setErr(null);
          setInfo(r.noop ? null : 'Swapped ' + r.from + ' → ' + r.to + ' (repriced)');
        } else { setInfo(null); setErr(r?.error || 'No SKU for that term on this vehicle'); }
      })
      .catch((e) => { setInfo(null); setErr(errMsg(e)); });
  };

  const onAddSku = (result) => {
    return apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines`, { sku: result.sku, qty: 1 })
      .then((r) => applyResult(r, 'Could not add line'))
      .catch((e) => setErr(errMsg(e)));
  };

  // Bulk paste from a sheet (parsed rows from the shared omnibox/canvas).
  const onBulkAdd = (items, groupTitle) => {
    setInfo('Adding ' + items.length + ' pasted line' + (items.length === 1 ? '' : 's') + '…');
    return apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/bulk`, { items, groupTitle: groupTitle || null })
      .then((r) => {
        if (r?.quote) {
          if (applyQuote(r.quote)) setErr(null);
          const missing = (r.report || []).filter((x) => x.status !== 'priced');
          setInfo('Added ' + (r.report || []).length + ' lines from paste'
            + (r.groupTitle ? ' into group “' + r.groupTitle + '”' : ''));
          if (missing.length) setPasteReport({ skus: missing.map((x) => x.sku) });
        } else { setInfo(null); setErr(r?.error || 'Paste import failed'); }
      })
      .catch((e) => { setInfo(null); setErr(errMsg(e)); });
  };

  const onAddBundle = (bundle, size) => {
    setInfo('Adding ' + bundle.name + '…');
    return apiClient.post(`/api/v1/quotes/${quote.quoteId}/bundle`, { bundleId: Number(bundle.bundleId), sizingValue: size })
      .then((r) => {
        if (r?.quote) {
          if (applyQuote(r.quote)) setErr(null);
          const report = r.report || [];
          const added = report.filter((x) => x.status === 'added').length;
          const issues = report.filter((x) => x.status !== 'added');
          setInfo('Added ' + added + ' line' + (added === 1 ? '' : 's') + ' from ' + (r.bundleName || bundle.name)
            + (issues.length ? ' — needs attention: ' + issues.map((x) => `${x.component} (${x.status})`).join('; ') : ''));
        } else { setInfo(null); setErr(r?.error || 'Could not add bundle'); }
      })
      .catch((e) => { setInfo(null); setErr(errMsg(e)); });
  };

  const onLineChange = (lineId, patch) => {
    setQuote((prev) => ({ ...prev, lines: prev.lines.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)) }));
    trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}/lines/${lineId}`, { method: 'PATCH', body: patch }), quote.quoteId);
  };

  const onRemoveLine = (lineId) => {
    setQuote((prev) => ({ ...prev, lines: prev.lines.filter((l) => l.lineId !== lineId) }));
    trackSave(apiClient.del(`/api/v1/quotes/${quote.quoteId}/lines/${lineId}`), quote.quoteId);
  };

  const onReorderLines = (lineIds) => {
    setQuote((prev) => {
      const byId = new Map(prev.lines.map((l) => [l.lineId, l]));
      const lines = lineIds.map((id) => byId.get(id)).filter(Boolean);
      trackSave(apiClient.post(`/api/v1/quotes/${prev.quoteId}/lines/reorder`, { lineIds }), prev.quoteId);
      return { ...prev, lines };
    });
  };

  const onAddHeader = (title, position) => {
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/header`,
      Number.isInteger(position) ? { title, position } : { title })
      .then((r) => applyResult(r, 'Could not add header'))
      .catch((e) => setErr(errMsg(e)));
  };

  const onAddSection = (title, position) => {
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/section`,
      Number.isInteger(position) ? { title, position } : { title })
      .then((r) => applyResult(r, 'Could not add section'))
      .catch((e) => setErr(errMsg(e)));
  };

  const onDuplicateBlock = (lineId) => {
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/${lineId}/duplicate`)
      .then((r) => applyResult(r, 'Could not duplicate'))
      .catch((e) => setErr(errMsg(e)));
  };

  // Layer-1 quote review: deterministic rule findings from the backend.
  const onValidate = () => {
    setValidation('loading');
    apiClient.get(`/api/v1/quotes/${quote.quoteId}/validation`)
      .then((r) => setValidation(r && r.findings ? r : null))
      .catch((e) => { setValidation(null); setErr(errMsg(e)); });
  };

  // Apply a finding's resolved fix: add-sku via the normal add-line
  // path; set-qty via parallel line PATCHes + one refetch for server truth.
  const onApplyFix = async (fix) => {
    if (fix.kind === 'set-qty') {
      const lines = fix.lines || [];
      const results = await Promise.allSettled(
        lines.map((ln) =>
          apiClient.request(`/api/v1/quotes/${quote.quoteId}/lines/${ln.lineId}`,
            { method: 'PATCH', body: { qty: fix.qty } })
        )
      );
      const failed = results.filter((r) => r.status === 'rejected' || r.value?.error).length;
      // One refetch after all settle so the table matches server truth
      // (any partial failure leaves earlier PATCHes applied server-side).
      const fresh = await apiClient.get(`/api/v1/quotes/${quote.quoteId}`).catch(() => null);
      if (fresh?.quote) applyQuote(fresh.quote);
      if (failed > 0) {
        setErr(`Quantity update failed for ${failed} of ${lines.length} line${lines.length === 1 ? '' : 's'}`);
        return null;
      }
      setErr(null);
      return fresh;
    }
    const payload = { sku: fix.sku, qty: fix.qty };
    if (fix.afterLineId) payload.afterLineId = fix.afterLineId;  // land beneath the paired hardware
    // Per-year priced SKUs co-term to the platform's years.
    if (fix.pricedPerYear && fix.termYears > 1) payload.termMonths = fix.termYears * 12;
    return apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines`, payload)
      .then((r) => applyResult(r, `Could not add ${fix.sku}`))
      .catch((e) => setErr(errMsg(e)));
  };

  // Coverage sweep: one support level / one licensing bundle everywhere.
  const onLoadCoverageOptions = () =>
    apiClient.get(`/api/v1/quotes/${quote.quoteId}/coverage-options`)
      .then((r) => (r && r.platforms ? r : null))
      .catch(() => null);

  const onApplyCoverage = (sel) =>
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/coverage`, sel)
      .then((r) => {
        if (r?.quote) { if (applyQuote(r.quote)) setErr(null); }
        else setErr(r?.error || 'Could not apply coverage');
      })
      .catch((e) => setErr(errMsg(e)));

  const onAddCustomLine = (payload) => {
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/custom`, payload)
      .then((r) => applyResult(r, 'Could not add custom line'))
      .catch((e) => setErr(errMsg(e)));
  };

  const onRestoreLine = (line) => {
    apiClient.post(`/api/v1/quotes/${quote.quoteId}/lines/restore`, { line })
      .then((r) => applyResult(r, 'Could not restore line'))
      .catch((e) => setErr(errMsg(e)));
  };

  const onScenarioSelect = (name) => {
    setQuote((prev) => ({ ...prev, activeScenario: name }));
    trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}`, { method: 'PATCH', body: { activeScenario: name } }), quote.quoteId);
  };

  const onScenarioChange = (name, patch) => {
    setQuote((prev) => {
      const scenarios = prev.scenarios.map((s) => (s.name === name ? { ...s, ...patch } : s));
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        trackSave(apiClient.request(`/api/v1/quotes/${prev.quoteId}`, { method: 'PATCH', body: { scenarios } }), prev.quoteId);
      }, 600);
      return { ...prev, scenarios };
    });
  };

  const onPartnerChange = (partnerName) => {
    setQuote((prev) => ({ ...prev, partnerName }));
    trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}`, { method: 'PATCH', body: { partnerName } }), quote.quoteId);
  };

  const onQuoteNameChange = (name) => {
    setQuote((prev) => ({ ...prev, name }));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      trackSave(apiClient.request(`/api/v1/quotes/${quote.quoteId}`, { method: 'PATCH', body: { name } }), quote.quoteId);
    }, 600);
  };

  // Export: CSV here; the Google Sheets export (SpreadsheetApp) lives in
  // the GAS app, which shares the same quotes.
  const onExport = (format) => {
    if (!quote?.lines?.length) return;
    if (format === 'sheet') {
      setInfo('Sheets export runs in the GAS dashboard (same quote is there) — use CSV here.');
      return;
    }
    const scenario = quote.scenarios.find((s) => s.name === quote.activeScenario) || quote.scenarios[0];
    let economics;
    try { economics = computeQuoteEconomics(quote.lines, scenario, { taxRatePct: quote.taxRatePct ?? null }); }
    catch (e) { setErr('Cannot export: ' + errMsg(e)); return; }
    const econById = new Map(economics.lines.map((e) => [e.lineId, e]));
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
    const fmtPctCsv = (f) => (f == null ? '' : (Math.round(f * 1000) / 10) + '%');
    const rows = [['Product Code', 'Description', 'Quantity', 'Sales Price', 'Discount % (vs list)', 'List Price', 'Line Total', 'Term (Months)', 'Contract']];
    const csvFlags = segmentEnabled(quote.lines);
    // Parked (qty-0) lines never ride along on an export — safety.
    quote.lines.filter((l, i) => csvFlags[i] && l.kind !== 'header' && l.kind !== 'section' && Number(l.qty) > 0).forEach((l) => {
      const e = econById.get(l.lineId) || {};
      rows.push([l.sku, l.description || '', l.qty, e.customerUnit ?? '', fmtPctCsv(e.customerOffListPct), l.listUnit ?? '', e.customerExtended ?? '', l.termMonths || '', l.contractId]);
    });
    rows.push([]);
    rows.push(['This is an estimate, not a quote. Formal quotes are issued by the authorized resellers. Pricing is an estimation based on standard ' + (quote.vehicle === 'ETC' ? 'California Enterprise Technology Contract (ETC)' : quote.vehicle) + ' pricing and is subject to change.']);
    const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = (quote.name || 'quote').replace(/[^\w.-]+/g, '_') + '.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const resellers = useMemo(() => {
    if (!quote) return [];
    return vehicles.find((v) => v.contractId === quote.contractId)?.resellers || [];
  }, [quote, vehicles]);

  // ── Builder ──
  if (quote) {
    const pricingAgeDays = quote.pricingAsOf
      ? Math.floor((Date.now() - new Date(quote.pricingAsOf).getTime()) / 86400000)
      : null;
    return (
      <div className="qp-root qp-builder">
        <div className="qp-header-strip">
          <button type="button" onClick={closeQuote} className="qp-btn">← All quotes</button>
          {pricingAgeDays !== null && pricingAgeDays > 60 && (
            <span className="qp-warn">
              ⚠ Price snapshot is {pricingAgeDays} days old — the GPL may have been revised since.
            </span>
          )}
          {info && <span className="qp-note" role="status">{info}</span>}
          {pasteReport && (
            <span className="qp-paste-chip" role="status">
              ⚠ No contract price for: {pasteReport.skus.join(', ')}
              <button type="button" aria-label="Dismiss" onClick={() => setPasteReport(null)}>✕</button>
            </span>
          )}
          {err && <span className="qp-warn" role="alert">{err}</span>}
        </div>
        {/* QB-13: name the surface so the missing controls read as
            deliberate, not broken. Customer/partner email drafts, SFDC
            linking, and the branded Sheets/PDF files live in the in-tenant
            GAS cockpit (they touch restricted data); here it's build + CSV. */}
        <div className="qp-surface-note" role="note">
          Building surface — export is CSV here. Customer &amp; partner email drafts, SFDC
          linking, and branded estimate files are in the in-tenant cockpit.
        </div>
        <div className="qp-builder-body">
          <QuoteBuilder
            quote={quote}
            hydrating={!!quote.__hydrating}
            skuRules={skuRules}
            iconsBaseUrl={iconsBaseUrl}
            onResolveAttach={onResolveAttach}
            onLineTermChange={onLineTermChange}
            catalogQuery={catalogQuery}
            catalogResults={catalogResults}
            catalogLoading={catalogLoading}
            bundles={bundles}
            resellers={resellers}
            vehicles={vehicles}
            onVehicleChange={onVehicleChange}
            catalogTree={catalogTree}
            browse={browse}
            onBrowseSelect={onBrowseSelect}
            onOpportunityIdChange={onOpportunityIdChange}
            onDealMetaChange={onDealMetaChange}
            onCatalogQueryChange={onCatalogQueryChange}
            onAddSku={onAddSku}
            onAddBundle={onAddBundle}
            onBulkAdd={onBulkAdd}
            onLineChange={onLineChange}
            onRemoveLine={onRemoveLine}
            onRestoreLine={onRestoreLine}
            onReorderLines={onReorderLines}
            onAddCustomLine={onAddCustomLine}
            onAddHeader={onAddHeader}
            onAddSection={onAddSection}
            onDuplicateBlock={onDuplicateBlock}
            validation={validation}
            onValidate={onValidate}
            onDismissValidation={() => setValidation(null)}
            onApplyFix={onApplyFix}
            onLoadCoverageOptions={onLoadCoverageOptions}
            onApplyCoverage={onApplyCoverage}
            specsBySku={specsBySku}
            onRequestSpecs={onRequestSpecs}
            gplDriftBySku={gplDriftBySku}
            gplDriftCheckedAt={gplDriftCheckedAt}
            catalogReferenceOnly={catalogReferenceOnly}
            saveState={saveState}
            onScenarioSelect={onScenarioSelect}
            onScenarioChange={onScenarioChange}
            onPartnerChange={onPartnerChange}
            onDuplicateForPartners={(partners) => apiClient.post(`/api/v1/quotes/${quote.quoteId}/variants`, { partners }).then((r) => {
              refreshList();
              setInfo(`Created ${(r?.created || []).length} partner variant(s) — see the deal canvas`);
              return r;
            })}
            onQuoteNameChange={onQuoteNameChange}
            onExport={onExport}
            exportFormats={['csv']}
            exportsHistory={quoteExports}
          />
        </div>
      </div>
    );
  }

  // ── Deal canvas (the intermediary: list → CANVAS → builder) ──
  if (dealOpp !== null && quotes) {
    const dealQuotes = dealOpp.opp
      ? quotes.filter((q) => q.sfOpportunityId === dealOpp.opp)
      : quotes.filter((q) => q.quoteId === dealOpp.solo);
    return (
      <div className="qp-root qp-builder">
        <div className="qp-builder-body">
          <DealCanvas
            sfOpportunityId={dealOpp.opp || null}
            deptAcronym={dealQuotes[0]?.deptAcronym || null}
            quotes={dealQuotes.map((q) => ({ ...q, customerTotal: q.customerTotal ?? null }))}
            onOpenQuote={openQuote}
            openingQuoteId={openingId}
            onNewExperiment={(fromId) => duplicateQuote(fromId)}
            onBack={() => setDealOpp(null)}
            formatCurrency={(n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}
            incumbents={dealFootprint === undefined ? undefined : (dealFootprint === null ? null : dealFootprint.competitorFootprint || [])}
            incumbentsPeriodYears={(dealFootprint && dealFootprint.period?.years) || 3}
            incumbentsError={footprintErr}
            comparisonTarget={cmpTarget}
            onSelectIncumbent={selectIncumbent}
            onCompareTco={(qid, vendor) => apiClient.get(`/api/v1/quotes/${qid}/tco-compare?vendor=${encodeURIComponent(vendor)}`)}
            onExportPartnerPack={(doc) => {
              // TekStak has no Sheets: download the (already-redacted) pack as markdown.
              const md = [
                `# ${doc.title}`, doc.subtitle, `**${doc.confidentiality}**`, '',
                ...doc.sections.flatMap((s) => [
                  s.heading ? `## ${s.heading}` : '',
                  ...(s.kind === 'table' && s.rows?.length
                    ? [s.rows[0].join(' | '), s.rows[0].map(() => '---').join(' | '), ...s.rows.slice(1).map((r) => r.join(' | '))]
                    : (s.items || []).map((it) => (s.kind === 'bullets' ? `- ${it}` : it))),
                  '',
                ]),
                '---', ...doc.footer.map((f) => `_${f}_`),
              ].join('\n');
              const blob = new Blob([md], { type: 'text/markdown' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `${doc.title.replace(/[^\w-]+/g, '-')}.md`;
              a.click();
              URL.revokeObjectURL(a.href);
              return Promise.resolve();
            }}
            onExportProposal={(payload) => downloadTcoProposalCsv(payload)}
          />
        </div>
      </div>
    );
  }

  // ── List ──
  return (
    <div className="qp-root qp-page">
      <PageHeader
        title="Deal Construction"
        blurb="The canvas for constructing deals — quotes are the experiments. Shared with the GAS dashboard."
      />
      {err && <div className="qp-error" role="alert">{err}</div>}

      <div className="qp-toolbar">
        <input
          type="search"
          className="qp-input qp-search"
          value={quoteSearch}
          onChange={(e) => setQuoteSearch(e.target.value)}
          placeholder="Search quotes… (name, agency, vehicle)"
        />
        <button type="button" onClick={() => setCreating((c) => !c)} className="qp-btn qp-btn--primary">
          {creating ? 'Cancel' : '+ New quote'}
        </button>
      </div>

      {creating && (
        <div className="qp-dept-step">
          <div className="qp-dept-label">Who is this quote for?</div>
          <input
            autoFocus
            className="qp-input"
            style={{ width: '100%' }}
            value={deptQuery}
            onChange={(e) => searchDepts(e.target.value)}
            placeholder="Search the customer agency…"
          />
          {deptResults.length > 0 && (
            <div className="qp-dept-results">
              {deptResults.map((d) => (
                <div key={d.acronym} className="qp-dept-row" onClick={() => createQuoteFor(d)}>
                  <strong>{d.acronym}</strong> — {d.name || d.official_name || ''}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {quotes === null && <p>Loading quotes…</p>}
      {quotes !== null && quotes.length === 0 && <p className="qp-note">No quotes yet — create one above, or in the GAS dashboard.</p>}
      {quotes !== null && quotes.length > 0 && (() => {
        const visible = quoteSearch
          ? quotes.filter((q) => fuzzyMatch(quoteSearch, [q.name, q.estimateNo, (q.estimateNo || '').replace(/^EST-/, 'Q-'), q.sfOpportunityId, q.deptAcronym, q.vehicle, q.status].join(' ')))
          : quotes;
        if (!visible.length) return <p className="qp-note">No quotes match “{quoteSearch}”.</p>;

        // Deals-first: opps with 2+ constructions group under a deal row.
        const byOpp = new Map();
        visible.forEach((q) => {
          if (!q.sfOpportunityId) return;
          if (!byOpp.has(q.sfOpportunityId)) byOpp.set(q.sfOpportunityId, []);
          byOpp.get(q.sfOpportunityId).push(q);
        });
        const dealOpps = [...byOpp.keys()].filter((k) => byOpp.get(k).length >= 2)
          .sort((a, b) => {
            const la = byOpp.get(a).reduce((m, q) => (q.updatedAt > m ? q.updatedAt : m), '');
            const lb = byOpp.get(b).reduce((m, q) => (q.updatedAt > m ? q.updatedAt : m), '');
            return lb.localeCompare(la);
          });
        const grouped = new Set(dealOpps);
        const singles = visible.filter((q) => !q.sfOpportunityId || !grouped.has(q.sfOpportunityId));
        const fmtUsd0 = (n) => '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

        // Canvas earns its place: opp-linked quotes open the canvas;
        // unlinked solo quotes go straight to the document.
        const openDealFor = (q) => (q.sfOpportunityId ? setDealOpp({ opp: q.sfOpportunityId }) : openQuote(q.quoteId));
        // Same family ordering + derived dimming as the deal canvas.
        const orderWithVariants = (arr) => {
          const byId = new Map(arr.map((q) => [q.quoteId, q]));
          const variantsByRoot = new Map();
          arr.forEach((q) => {
            if (q.variantOf && byId.has(q.variantOf)) {
              if (!variantsByRoot.has(q.variantOf)) variantsByRoot.set(q.variantOf, []);
              variantsByRoot.get(q.variantOf).push(q);
            }
          });
          const out = [];
          arr.forEach((q) => {
            if (q.variantOf && byId.has(q.variantOf)) return;
            const kids = variantsByRoot.get(q.quoteId) || [];
            const winner = [q, ...kids].find((v) => v.registrationStatus === 'approved') || null;
            out.push({ q, isVariant: false, dimmed: !!(winner && winner.quoteId !== q.quoteId) });
            kids.forEach((v) => out.push({ q: v, isVariant: true, dimmed: !!(winner && winner.quoteId !== v.quoteId) }));
          });
          return out;
        };

        const renderRow = (q, inDeal, vm) => (
          <tr
            key={q.quoteId}
            tabIndex={0}
            title="Open the deal canvas"
            className={((openingId === q.quoteId ? 'qp-row-opening ' : '') + (flashId === q.quoteId ? 'qp-row-flash ' : '') + (inDeal ? 'qp-deal-member ' : '') + (vm && vm.isVariant ? 'qp-variant-row ' : '') + (vm && vm.dimmed ? 'qp-variant-dimmed' : '')).trim() || undefined}
            onClick={() => openDealFor(q)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDealFor(q); } }}
          >
            <td style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}>{q.estimateNo ? q.estimateNo.replace(/^EST-/, 'Q-') : '—'}</td>
            <td className="qp-name" style={{ paddingLeft: (inDeal ? 26 : 0) + (vm && vm.isVariant ? 22 : 0) || undefined }}>
              {openingId === q.quoteId && <span className="qp-row-spinner" aria-hidden />}
              {vm && vm.isVariant && <span className="qp-variant-arrow" aria-hidden>↳ </span>}
              {q.name}
              {vm && vm.isVariant && q.partnerName && (
                <span className={'qp-variant-partner' + (q.registrationStatus === 'approved' ? ' is-winner' : '')}>
                  {q.partnerName}{q.registrationStatus === 'approved' ? ' · registered' : ''}
                </span>
              )}
            </td>
            <td className="qp-num">{q.customerTotal != null ? fmtUsd0(q.customerTotal) : '—'}</td>
            <td onClick={(e) => e.stopPropagation()}>
              {q.sfOpportunityId && !inDeal ? (
                <button type="button" className="qp-btn" title="Open the deal canvas" onClick={() => setDealOpp({ opp: q.sfOpportunityId })}>
                  {q.sfOpportunityId}
                </button>
              ) : (inDeal ? '·' : '—')}
            </td>
            <td>{q.vehicle}</td>
            <td>{q.deptAcronym || '—'}</td>
            <td>{q.lineCount}</td>
            <td>{q.status}</td>
            <td>{(q.updatedAt || '').slice(0, 10)}</td>
            <td className="ts-row-actions" onClick={(e) => e.stopPropagation()}>
              <button type="button" title="Duplicate" className="qp-btn" disabled={dupPendingId === q.quoteId} onClick={() => duplicateQuote(q.quoteId)}>
                {dupPendingId === q.quoteId ? <span className="qp-row-spinner" aria-hidden /> : '⧉'}
              </button>{' '}
              <button
                type="button"
                title={confirmDelId === q.quoteId ? 'Click again to delete' : 'Delete'}
                className={'qp-btn' + (confirmDelId === q.quoteId ? ' qp-del-armed ts-armed' : '')}
                onClick={() => requestDelete(q.quoteId)}
              >
                {confirmDelId === q.quoteId ? 'Delete?' : '✕'}
              </button>
            </td>
          </tr>
        );

        return (
        <div className="qp-table-wrap">
        <table className="qp-table">
          <thead>
            <tr>
              <th>Quote #</th><th>Name</th><th className="qp-num">Total</th><th>SFDC Opp</th><th>Vehicle</th><th>Agency</th>
              <th>Lines</th><th>Status</th><th>Updated</th><th />
            </tr>
          </thead>
          <tbody>
            {dealOpps.flatMap((opp) => {
              const qs = byOpp.get(opp);
              const best = qs.reduce((m, q) => (q.customerTotal != null && (m === null || q.customerTotal > m) ? q.customerTotal : m), null);
              const latest = qs.reduce((m, q) => (q.updatedAt > m ? q.updatedAt : m), '');
              return [
                <tr
                  key={'deal-' + opp}
                  tabIndex={0}
                  className="qp-deal-row"
                  title="Open the deal canvas"
                  onClick={() => setDealOpp({ opp })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDealOpp({ opp }); } }}
                >
                  <td colSpan={2}><span className="qp-deal-badge">DEAL</span> <b>{opp}</b></td>
                  <td className="qp-num" style={{ fontWeight: 700 }}>{best != null ? fmtUsd0(best) : '—'}</td>
                  <td colSpan={5} className="qp-note">
                    {(() => { const roots = qs.filter((x) => !x.variantOf).length; const vars = qs.length - roots; return `${roots} construction${roots === 1 ? '' : 's'}${vars ? ` · ${vars} partner variant${vars === 1 ? '' : 's'}` : ''}`; })()} · best shown{qs[0].deptAcronym ? ' · ' + qs[0].deptAcronym : ''}
                  </td>
                  <td>{latest.slice(0, 10)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="qp-btn" onClick={() => setDealOpp({ opp })}>Canvas</button>
                  </td>
                </tr>,
                ...orderWithVariants(qs).map(({ q, isVariant, dimmed }) => renderRow(q, true, { isVariant, dimmed })),
              ];
            })}
            {orderWithVariants(singles).map(({ q, isVariant, dimmed }) => renderRow(q, false, { isVariant, dimmed }))}
          </tbody>
        </table>
        </div>
        );
      })()}
      {removedQuote && (
        <div className="qp-undo-toast" role="status">
          Deleted “{removedQuote.name}”
          <button type="button" onClick={undoDeleteQuote}>Undo</button>
        </div>
      )}
    </div>
  );
}
